import React from "react";

import type { PickemsCompetitor } from "@/hooks/firestore/models/pickems";
import type { BettingCompetitor } from "hooks/firestore/models/bettingCompetitor";

import cx from "classnames";
import { buildCompetitorAvatarUrl } from "@/features/betting/util/competitorAvatarUrl";

import * as Avatar from "@radix-ui/react-avatar";
import * as styles from "./CompetitorAvatar.module.scss";

type CompetitorAvatarProps = {
  competitor?: BettingCompetitor | PickemsCompetitor;
  className?: string;
  reserveAvatarSpace?: boolean;
  /** explicit square size in px — inline, so it beats class-based sizing */
  size?: number;
};

export const CompetitorAvatar = ({
  competitor,
  className,
  reserveAvatarSpace = false,
  size,
}: CompetitorAvatarProps) => {
  const sizeStyle =
    size === undefined ? undefined : { width: size, height: size };

  if (!competitor) {
    if (reserveAvatarSpace) {
      return (
        <div
          className={cx(styles.avatar, styles.blank, className)}
          style={sizeStyle}
        />
      );
    } else {
      return null;
    }
  }

  const isFlag = !competitor.iconUri && !!competitor.nationality;
  const iconUrl = buildCompetitorAvatarUrl(competitor, 48);

  const name = competitor.abbreviation || competitor.name;

  return (
    <Avatar.Root
      className={cx(styles.avatar, { [styles.flag]: isFlag }, className)}
      title={competitor.name}
      style={sizeStyle}
    >
      <Avatar.Image
        src={iconUrl}
        alt={competitor.name}
        style={sizeStyle}
        className={cx({ [styles.flag]: isFlag })}
      />
      <Avatar.Fallback
        className={cx(styles.avatar, styles.fallback, className)}
        style={sizeStyle}
      >
        {name?.substring(0, 3).toUpperCase()}
      </Avatar.Fallback>
    </Avatar.Root>
  );
};
